import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { BACKEND_URL } from "../../config";

// Position AI Chat ("Service Kai") — feature #9. Talks to the Portal-BFF
// position-ai-chat endpoints (all POST, all checkUser-gated). Each "assistant"
// is one AI Prompts row; the BFF only returns the ones the caller's position(s)
// grant access to, so the client never has to know about the gate.
export const positionAiApi = createApi({
  reducerPath: "positionAiApi",
  tagTypes: ["PositionAiHistory"],
  baseQuery: fetchBaseQuery({
    baseUrl: BACKEND_URL,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.token;
      if (token) {
        headers.set("authorization", `Bearer ${token}`);
      }
      return headers;
    },
  }),
  endpoints: (builder) => ({
    // The assistants this employee's position(s) can see. Drives whether the
    // Service Kai FAB renders at all (hidden when the list is empty).
    listAssistants: builder.query({
      query: () => ({
        url: "/position-ai-chat/portal/list-assistants",
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => res?.data || [],
    }),

    // Durable, scrollable history for one assistant (oldest-first).
    getChatHistory: builder.query({
      query: (assistantId) => ({
        url: "/position-ai-chat/portal/history",
        method: "POST",
        body: { assistantId },
      }),
      transformResponse: (res) => res?.data || [],
      providesTags: (result, error, assistantId) => [
        { type: "PositionAiHistory", id: assistantId },
      ],
    }),

    // Post a question. The BFF stores the question (MySQL-first), calls Gemini,
    // stores the reply, and returns the reply. We invalidate this assistant's
    // history so the thread refetches with both new turns.
    sendMessage: builder.mutation({
      query: ({ assistantId, message }) => ({
        url: "/position-ai-chat/portal/message",
        method: "POST",
        body: { assistantId, message },
      }),
      invalidatesTags: (result, error, { assistantId }) => [
        { type: "PositionAiHistory", id: assistantId },
      ],
    }),

    // Wipe this assistant's entire history for the logged-in employee. The BFF
    // deletes only the caller's own rows; we invalidate so the thread refetches
    // (and renders the empty state).
    clearChat: builder.mutation({
      query: (assistantId) => ({
        url: "/position-ai-chat/portal/clear",
        method: "POST",
        body: { assistantId },
      }),
      invalidatesTags: (result, error, assistantId) => [
        { type: "PositionAiHistory", id: assistantId },
      ],
    }),

    // Rep/team names for the composer autocomplete (feature #14). Served from a
    // server-side cache; the drawer preloads this ONCE on open and filters it
    // client-side, so there is no network call per keystroke. RTK keeps the
    // result cached for the session, so reopening the drawer doesn't refetch.
    getChatNames: builder.query({
      query: () => ({
        url: "/position-ai-chat/portal/names",
        method: "POST",
        body: {},
      }),
      transformResponse: (res) => res?.data || [],
    }),
  }),
});

export const {
  useListAssistantsQuery,
  useGetChatHistoryQuery,
  useSendMessageMutation,
  useClearChatMutation,
  useGetChatNamesQuery,
} = positionAiApi;
